import {
  FieldType,
  ProductField,
  type ProductFieldConfig,
  type TextConfig,
  type NumberConfig,
  type PriceConfig,
  type DateConfig,
  type CannabisConfig,
  type AssetFieldConfig,
  type PimFieldValue,
  type PimScalarValue,
  type PimOptionValue,
  type PimMultiOptionValue,
  type PimAssetsValue,
  type PimAssetEntry,
  type FieldSource,
} from "@thenounproject/lingo-core";
import { getBaseFieldType } from "./fieldTypeRegistry";

export type FieldOption = {
  id: string;
  name: string;
};

export type FieldErrors = {
  name?: string;
  options?: string;
};

export type ProductFieldFormData = {
  /** Set only for existing fields loaded from the backend — identifies them in a bulk sync. */
  uuid?: string;
  /** Stable client-side ID for drag-and-drop ordering when uuid is not yet available. */
  tempId?: string;
  name: string;
  fieldType: FieldType;
  visibility?: "public" | "private";
  options?: FieldOption[];
  config?: ProductFieldConfig;
  errors?: FieldErrors;
  isExpanded?: boolean;
};

export const defaultSystemFields: ProductFieldFormData[] = [
  { name: "Images", fieldType: "images", visibility: "public" },
  { name: "Name", fieldType: "name", visibility: "public" },
  { name: "Description", fieldType: "description", visibility: "public" },
  { name: "SKU", fieldType: "sku", visibility: "public" },
  { name: "Price", fieldType: "price", visibility: "public" },
];

// --- Field config types ---

export type FieldConfigProps = {
  config?: ProductFieldConfig;
  overrideConfig?: Partial<ProductFieldConfig>;
  onChange: (key: string, value: string | number | boolean) => void;
  options?: FieldOption[];
  onOptionsChange?: (options: FieldOption[]) => void;
  optionErrors?: string;
};

// --- Typed config getters with defaults ---

function safeDecimals(value: unknown, defaultValue: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.round(value))
    : defaultValue;
}

export function getTextConfig(config?: ProductFieldConfig): Required<TextConfig> {
  return {
    multiline: config?.multiline ?? false,
  };
}

export function getNumberConfig(config?: ProductFieldConfig): Required<NumberConfig> {
  const numberType = config?.numberType;
  return {
    numberType:
      numberType === "default" || numberType === "currency" || numberType === "percentage"
        ? numberType
        : "default",
    decimals: safeDecimals(config?.decimals, 2),
    thousandsSeparator: config?.thousandsSeparator ?? false,
    currency: config?.currency ?? "USD",
  };
}

export function getPriceConfig(config?: ProductFieldConfig): Required<PriceConfig> {
  return {
    currency: config?.currency ?? "USD",
    decimals: safeDecimals(config?.decimals, 2),
    thousandsSeparator: config?.thousandsSeparator ?? false,
  };
}

export function getDateConfig(config?: ProductFieldConfig): Required<DateConfig> {
  const timeFormat = config?.timeFormat;
  return {
    dateFormat: config?.dateFormat ?? "YYYY-MM-DD",
    includeTime: config?.includeTime ?? false,
    timeFormat: timeFormat === "24h" || timeFormat === "12h" ? timeFormat : "24h",
  };
}

export function getCannabisConfig(config?: ProductFieldConfig): Required<CannabisConfig> {
  return {
    parentCategory: config?.parentCategory ?? "",
  };
}

export function getAssetConfig(config?: ProductFieldConfig): Required<AssetFieldConfig> {
  return {
    maxAssets: typeof config?.maxAssets === "number" ? config.maxAssets : 0,
    assetTypes: Array.isArray(config?.assetTypes) ? config.assetTypes : [],
  };
}

/** Canonical "images" field identifier — matches the API's built-in field. */
export const IMAGES_IDENTIFIER = "images";

/**
 * Pick the identifier of the asset-typed field the UI should treat as the product's
 * primary image source. Prefers `images`, otherwise falls back to the first
 * `asset`-typed field in display order.
 */
export function getPrimaryAssetIdentifier(fields: ProductField[]): string | null {
  const images = fields.find(f => f.identifier === IMAGES_IDENTIFIER);
  if (images) return images.identifier;
  const asset = fields.find(f => getBaseFieldType(f.fieldType) === "asset");
  return asset?.identifier ?? null;
}

// --- PIM field value read/write helpers ---

export function isInherited(v: PimFieldValue | undefined): boolean {
  return v == null || v.source === "inherited";
}

export function isOverride(v: PimFieldValue | undefined): boolean {
  return v?.source === "override";
}

export function readScalar(v: PimFieldValue | undefined): string {
  if (!v || !("value" in v) || v.value == null) return "";
  return String(v.value);
}

export function readOption(v: PimFieldValue | undefined): string {
  if (!v || !("option" in v) || !v.option) return "";
  return v.option.value ?? "";
}

export function readOptions(v: PimFieldValue | undefined): string[] {
  if (!v || !("options" in v) || !Array.isArray(v.options)) return [];
  return v.options.map(o => o.value);
}

export function readAssets(v: PimFieldValue | undefined): PimAssetEntry[] {
  if (!v || !("assets" in v) || !Array.isArray(v.assets)) return [];
  return v.assets;
}

export function getAssetUuids(v: PimFieldValue | undefined): string[] {
  return readAssets(v).map(a => a.assetUuid);
}

export function makeScalar(
  value: string | number | boolean | null,
  source: FieldSource = null
): PimScalarValue {
  return { value, source };
}

export function makeOption(value: string | null, source: FieldSource = null): PimOptionValue {
  return { option: value ? { value } : null, source };
}

export function makeOptions(values: string[], source: FieldSource = null): PimMultiOptionValue {
  return { options: values.map(v => ({ value: v })), source };
}

export function makeAssets(
  entries: PimAssetEntry[] | string[],
  source: FieldSource = null
): PimAssetsValue {
  const assets: PimAssetEntry[] = entries.map((e, i) =>
    typeof e === "string" ? { assetUuid: e, displayOrder: i } : { ...e, displayOrder: i }
  );
  return { assets, source };
}

/** Make an empty field value appropriate for a field type. Used when a value is missing from a fresh product. */
export function makeEmptyFieldValue(
  fieldType: FieldType,
  source: FieldSource = null
): PimFieldValue {
  const base = getBaseFieldType(fieldType);
  switch (base) {
    case "select":
      return { option: null, source };
    case "multiselect":
      return { options: [], source };
    case "asset":
      return { assets: [], source };
    case "boolean":
      return { value: false, source };
    default:
      return { value: null, source };
  }
}

/** Return a deep-cloned field value with its source overwritten. Used for inherit/override transitions. */
export function withSource(v: PimFieldValue, source: FieldSource): PimFieldValue {
  // JSON clone so this works on immer draft proxies in the reducer.
  return { ...(JSON.parse(JSON.stringify(v)) as PimFieldValue), source };
}

/** Serialize a PimFields dict to the minimal API input shape (strips read-only props). */
export function toApiFields(
  fields: Record<string, PimFieldValue>
): Record<string, Record<string, unknown>> {
  return Object.fromEntries(
    Object.entries(fields).map(([productFieldId, v]) => [productFieldId, fieldValueToInput(v)])
  );
}

function fieldValueToInput(v: PimFieldValue): Record<string, unknown> {
  if ("value" in v) return { value: v.value };
  if ("option" in v) return { option: v.option ? { value: v.option.value } : null };
  if ("assets" in v) return { assets: v.assets.map(a => ({ asset_uuid: a.assetUuid })) };
  if ("options" in v) return { options: v.options.map(o => ({ value: o.value })) };
  return {};
}

/** Deep equality of two field values for unsaved-change / inherit-collapse detection. */
export function fieldValuesEqual(
  a: PimFieldValue | undefined,
  b: PimFieldValue | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if ("value" in a && "value" in b) {
    if (a.value === b.value) return true;
    if (a.value == null || b.value == null) return false;
    // The API serializes number fields as strings; handle the number/string mismatch.
    const [num, str] = typeof a.value === "number" ? [a.value, b.value] : [b.value, a.value];
    return (
      typeof num === "number" && typeof str === "string" && !!str.trim() && Number(str) === num
    );
  }
  if ("option" in a && "option" in b)
    return (a.option?.value ?? null) === (b.option?.value ?? null);
  if ("options" in a && "options" in b)
    return (
      a.options.length === b.options.length &&
      a.options.every((o, i) => o.value === b.options[i].value)
    );
  if ("assets" in a && "assets" in b)
    return (
      a.assets.length === b.assets.length &&
      a.assets.every((x, i) => x.assetUuid === b.assets[i].assetUuid)
    );
  return false;
}

// --- Config dropdown options ---

export const NUMBER_TYPE_OPTIONS = [
  { label: "Default", value: "default" },
  { label: "Currency", value: "currency" },
  { label: "Percentage", value: "percentage" },
];

export const CURRENCY_OPTIONS = [
  { label: "USD ($)", value: "USD" },
  { label: "EUR (€)", value: "EUR" },
  { label: "GBP (£)", value: "GBP" },
  { label: "CAD (C$)", value: "CAD" },
  { label: "AUD (A$)", value: "AUD" },
  { label: "JPY (¥)", value: "JPY" },
];

export const DATE_FORMAT_OPTIONS = [
  { label: "YYYY-MM-DD", value: "YYYY-MM-DD" },
  { label: "MM/DD/YYYY", value: "MM/DD/YYYY" },
  { label: "DD/MM/YYYY", value: "DD/MM/YYYY" },
];

export const TIME_FORMAT_OPTIONS = [
  { label: "24-hour", value: "24h" },
  { label: "12-hour", value: "12h" },
];

export const CANNABIS_PARENT_CATEGORY_OPTIONS = [
  { label: "All Categories", value: "" },
  { label: "Flower", value: "Flower" },
  { label: "Pre-Roll", value: "Pre-Roll" },
  { label: "Vaporizer", value: "Vaporizer" },
  { label: "Concentrate", value: "Concentrate" },
  { label: "Edible", value: "Edible" },
  { label: "Topical", value: "Topical" },
  { label: "Tincture", value: "Tincture" },
  { label: "Accessory", value: "Accessory" },
];

// --- Label formatting helpers ---

export function getFieldDisplayName(
  productFieldId: string,
  fieldDefinition: ProductField | undefined,
  effectiveConfig: ProductFieldConfig
): string {
  const baseName = fieldDefinition?.name || productFieldId;
  return getBaseFieldType(fieldDefinition?.fieldType) === "number"
    ? formatNumberFieldLabel(baseName, effectiveConfig)
    : baseName;
}

export function formatNumberFieldLabel(name: string, config: ProductFieldConfig): string {
  const { numberType, currency } = getNumberConfig(config);
  if (numberType === "currency") return `${currency} ${name}`;
  if (numberType === "percentage") return `${name} (%)`;
  return name;
}

// --- Display formatting helpers ---

export function formatDateValue(value: string, config: DateConfig): string {
  if (!value) return value;
  const { dateFormat = "YYYY-MM-DD", includeTime = false, timeFormat = "24h" } = config;

  let y: string, m: string, d: string, h24: number, min: string;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Date-only string — split directly, no timezone concern
    [y, m, d] = value.split("-");
    h24 = 0;
    min = "00";
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    // datetime-local format (no timezone suffix) — parse as local time
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    y = String(date.getFullYear());
    m = String(date.getMonth() + 1).padStart(2, "0");
    d = String(date.getDate()).padStart(2, "0");
    h24 = date.getHours();
    min = String(date.getMinutes()).padStart(2, "0");
  } else {
    // ISO timestamp with timezone (e.g. "...Z") — use UTC methods
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    y = String(date.getUTCFullYear());
    m = String(date.getUTCMonth() + 1).padStart(2, "0");
    d = String(date.getUTCDate()).padStart(2, "0");
    h24 = date.getUTCHours();
    min = String(date.getUTCMinutes()).padStart(2, "0");
  }

  const datePart =
    dateFormat === "MM/DD/YYYY"
      ? `${m}/${d}/${y}`
      : dateFormat === "DD/MM/YYYY"
        ? `${d}/${m}/${y}`
        : `${y}-${m}-${d}`;

  if (!includeTime) return datePart;

  const timePart =
    timeFormat === "12h"
      ? `${h24 % 12 || 12}:${min} ${h24 >= 12 ? "PM" : "AM"}`
      : `${String(h24).padStart(2, "0")}:${min}`;

  return `${datePart} ${timePart}`;
}

/**
 * Normalize a stored date string to the value format required by an HTML date input.
 * - type="date"           → YYYY-MM-DD
 * - type="datetime-local" → YYYY-MM-DDTHH:mm
 *
 * Values already in the target format are returned unchanged.
 * ISO timestamps that include a timezone (e.g. "...Z") are parsed and converted
 * using UTC methods so the displayed date matches what the server stored.
 */
export function toDateInputValue(raw: string, includeTime: boolean): string {
  if (!raw) return "";
  if (!includeTime) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  }
  // Strip seconds if present (inputs only allow HH:mm precision)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(raw)) return raw.slice(0, 16);
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const y = d.getUTCFullYear(),
    mo = String(d.getUTCMonth() + 1).padStart(2, "0"),
    day = String(d.getUTCDate()).padStart(2, "0"),
    h = String(d.getUTCHours()).padStart(2, "0"),
    min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${y}-${mo}-${day}T${h}:${min}`;
}

export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
};

const CURRENCY_LOCALES: Record<string, string> = {
  USD: "en-US",
  EUR: "de-DE",
  GBP: "en-GB",
  CAD: "en-CA",
  AUD: "en-AU",
  JPY: "ja-JP",
};

const separatorCache = new Map<string, { decimal: string; thousands: string }>();

function getCurrencySeparators(currency: string): { decimal: string; thousands: string } {
  if (separatorCache.has(currency)) return separatorCache.get(currency)!;
  const locale = CURRENCY_LOCALES[currency] ?? "en-US",
    parts = new Intl.NumberFormat(locale, { minimumFractionDigits: 1 }).formatToParts(1234.5),
    result = {
      decimal: parts.find(p => p.type === "decimal")?.value ?? ".",
      thousands: parts.find(p => p.type === "group")?.value ?? ",",
    };
  separatorCache.set(currency, result);
  return result;
}

export function formatCurrencyDisplay(
  raw: string,
  decimals: number,
  thousandsSeparator: boolean,
  currency: string
): string {
  if (!raw) return raw ?? "";
  const n = parseFloat(raw);
  if (isNaN(n)) return raw;
  const locale = CURRENCY_LOCALES[currency] ?? "en-US";
  let formatted = n.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  if (!thousandsSeparator) {
    const { thousands } = getCurrencySeparators(currency);
    formatted = formatted.split(thousands).join("");
  }
  return formatted;
}

export function formatPriceValue(value: string, config?: PriceConfig): string {
  return formatNumberValue(value, { numberType: "currency", ...config });
}

export function formatNumberValue(value: string, config: NumberConfig): string {
  const num = Number(value);
  if (isNaN(num)) return value;
  const {
    decimals = 2,
    numberType = "default",
    thousandsSeparator = false,
    currency = "USD",
  } = config;

  if (numberType === "currency") {
    const locale = CURRENCY_LOCALES[currency] ?? "en-US",
      { thousands } = getCurrencySeparators(currency),
      symbol = CURRENCY_SYMBOLS[currency] ?? currency;
    let formatted = num.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    if (!thousandsSeparator) formatted = formatted.split(thousands).join("");
    return `${symbol}${formatted}`;
  }

  const formatted = thousandsSeparator
    ? num.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : num.toFixed(decimals);

  if (numberType === "percentage") return `${formatted}%`;
  return formatted;
}
